<template>
    <div
        class="create-group-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-group-modal-title"
    >
        <div class="create-group-modal__dialog">
            <header class="create-group-modal__header">
                <h3
                    id="create-group-modal-title"
                    class="create-group-modal__title"
                >
                    Create Group Chat
                </h3>

                <button
                    type="button"
                    class="create-group-modal__close"
                    aria-label="Close create group popup"
                    :disabled="isSubmitting"
                    @click="closeModal"
                >
                    <i
                        class="fa fa-times"
                        aria-hidden="true"
                    ></i>
                </button>
            </header>

            <div class="create-group-modal__body">
                <div
                    v-if="isLoadingUsers"
                    class="create-group-modal__status"
                    role="status"
                >
                    <i
                        class="fa fa-spinner fa-spin"
                        aria-hidden="true"
                    ></i>

                    <span>Loading users...</span>
                </div>

                <div
                    v-else-if="userLoadError"
                    class="
                        create-group-modal__status
                        create-group-modal__status--error
                    "
                    role="alert"
                >
                    <i
                        class="fa fa-exclamation-circle"
                        aria-hidden="true"
                    ></i>

                    <span>{{ userLoadError }}</span>

                    <button
                        type="button"
                        class="create-group-modal__retry"
                        @click="loadUsers"
                    >
                        Retry
                    </button>
                </div>

                <form
                    v-else
                    class="create-group-form"
                    @submit.prevent="submitGroup"
                >
                    <div class="create-group-form__field">
                        <label
                            class="create-group-form__label"
                            for="group-name"
                        >
                            Group Name
                        </label>

                        <input
                            id="group-name"
                            v-model.trim="groupName"
                            v-validate="'required|max:100'"
                            type="text"
                            name="groupName"
                            data-vv-as="group name"
                            class="create-group-form__input"
                            :class="{
                                'create-group-form__input--invalid':
                                    errors.has('groupName')
                            }"
                            :disabled="isSubmitting"
                            maxlength="100"
                            autocomplete="off"
                        >

                        <small
                            v-if="errors.has('groupName')"
                            class="create-group-form__error"
                            role="alert"
                        >
                            {{ errors.first('groupName') }}
                        </small>
                    </div>

                    <div class="create-group-form__field">
                        <label class="create-group-form__label">
                            Participants
                        </label>

                        <multiselect
                            v-model="selectedUsers"
                            v-validate="'required'"
                            name="users"
                            class="create-group-form__multiselect"
                            :class="{
                                'create-group-form__multiselect--invalid':
                                    errors.has('users')
                            }"
                            placeholder="Add group participants"
                            track-by="id"
                            :options="userOptions"
                            :custom-label="getUserLabel"
                            :multiple="true"
                            :taggable="false"
                            :searchable="true"
                            :hide-selected="true"
                            :close-on-select="false"
                            :clear-on-select="false"
                            :allow-empty="true"
                            :disabled="isSubmitting"
                            :max-height="300"
                        >
                            <template
                                slot="option"
                                slot-scope="{ option }"
                            >
                                <div class="participant-option">
                                    <span class="participant-option__name">
                                        {{ getUserFullName(option) }}
                                    </span>

                                    <span
                                        v-if="option.email"
                                        class="participant-option__email"
                                    >
                                        {{ option.email }}
                                    </span>
                                </div>
                            </template>

                            <template
                                slot="tag"
                                slot-scope="{ option, remove }"
                            >
                                <span class="participant-tag">
                                    <span class="participant-tag__text">
                                        {{ getUserFullName(option) }}
                                    </span>

                                    <button
                                        type="button"
                                        class="participant-tag__remove"
                                        :aria-label="
                                            'Remove ' +
                                            getUserFullName(option)
                                        "
                                        @click.prevent="remove(option)"
                                    >
                                        ×
                                    </button>
                                </span>
                            </template>

                            <span slot="noResult">
                                No matching users found.
                            </span>

                            <span slot="noOptions">
                                No users are available.
                            </span>
                        </multiselect>

                        <small
                            v-if="errors.has('users')"
                            class="create-group-form__error"
                            role="alert"
                        >
                            {{ errors.first('users') }}
                        </small>

                        <small
                            v-else
                            class="create-group-form__hint"
                        >
                            {{ selectedUsersText }}
                        </small>
                    </div>

                    <div class="create-group-form__field">
                        <label class="create-group-form__label">
                            Group Image
                        </label>

                        <div class="create-group-form__image-row">
                            <div
                                v-if="croppedImagePreview"
                                class="create-group-form__image-preview"
                            >
                                <img
                                    :src="croppedImagePreview"
                                    alt="Selected group image preview"
                                >
                            </div>

                            <label
                                class="create-group-form__upload"
                                :class="{
                                    'create-group-form__upload--disabled':
                                        isSubmitting
                                }"
                                for="group-image-input"
                            >
                                <input
                                    id="group-image-input"
                                    ref="fileInput"
                                    type="file"
                                    class="create-group-form__file-input"
                                    accept="image/jpeg,image/png"
                                    :disabled="isSubmitting"
                                    @change="handleFileUpload"
                                >

                                <i
                                    class="fa fa-upload"
                                    aria-hidden="true"
                                ></i>

                                <span>
                                    {{
                                        selectedFileName ||
                                        'Select Image'
                                    }}
                                </span>
                            </label>

                            <button
                                v-if="croppedImage"
                                type="button"
                                class="create-group-form__remove-image"
                                :disabled="isSubmitting"
                                @click="removeSelectedImage"
                            >
                                Remove
                            </button>
                        </div>

                        <small
                            v-if="imageError"
                            class="create-group-form__error"
                            role="alert"
                        >
                            {{ imageError }}
                        </small>

                        <small
                            v-else
                            class="create-group-form__hint"
                        >
                            JPEG or PNG, maximum {{ maxFileSizeMb }} MB.
                        </small>
                    </div>

                    <p
                        v-if="submitError"
                        class="create-group-form__submit-error"
                        role="alert"
                    >
                        {{ submitError }}
                    </p>

                    <div class="create-group-form__actions">
                        <button
                            type="button"
                            class="
                                create-group-form__button
                                create-group-form__button--cancel
                            "
                            :disabled="isSubmitting"
                            @click="closeModal"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            class="
                                create-group-form__button
                                create-group-form__button--submit
                            "
                            :disabled="isSubmitting"
                        >
                            <i
                                v-if="isSubmitting"
                                class="fa fa-spinner fa-spin"
                                aria-hidden="true"
                            ></i>

                            <span>
                                {{
                                    isSubmitting
                                        ? 'Creating...'
                                        : 'Create Group'
                                }}
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <div
            v-if="imageSource"
            class="image-crop-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="image-crop-modal-title"
        >
            <div class="image-crop-modal__dialog">
                <header class="image-crop-modal__header">
                    <h3
                        id="image-crop-modal-title"
                        class="image-crop-modal__title"
                    >
                        Crop Group Image
                    </h3>
                </header>

                <div class="image-crop-modal__body">
                    <div class="image-crop-modal__cropper">
                        <vue-cropper
                            ref="cropper"
                            :src="imageSource"
                            :background="false"
                            :rotatable="true"
                            :scalable="true"
                            :zoomable="true"
                            :movable="true"
                            :crop-box-movable="true"
                            :crop-box-resizable="true"
                            :responsive="true"
                            :restore="false"
                            :view-mode="1"
                            :min-crop-box-width="150"
                            :min-crop-box-height="150"
                            :aspect-ratio="1"
                            alt="Crop selected group image"
                        />
                    </div>

                    <p
                        v-if="cropError"
                        class="image-crop-modal__error"
                        role="alert"
                    >
                        {{ cropError }}
                    </p>

                    <div class="image-crop-modal__actions">
                        <button
                            type="button"
                            class="
                                image-crop-modal__button
                                image-crop-modal__button--cancel
                            "
                            @click="cancelCrop"
                        >
                            <img
                                src="@/assets/images/remove.png"
                                alt=""
                                aria-hidden="true"
                            >

                            <span>Cancel</span>
                        </button>

                        <button
                            type="button"
                            class="
                                image-crop-modal__button
                                image-crop-modal__button--crop
                            "
                            @click="cropImage"
                        >
                            <img
                                src="@/assets/images/camera2.png"
                                alt=""
                                aria-hidden="true"
                            >

                            <span>Use Image</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import 'cropperjs/dist/cropper.css';
import 'vue-multiselect/dist/vue-multiselect.min.css';

import Multiselect from 'vue-multiselect';
import VueCropper from 'vue-cropperjs';
import { bus } from '@/main';
import toast from '@/services/toast';

const BODY_SCROLL_CLASS = 'body_right_scroll_remove';
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = [
    'image/jpeg',
    'image/png'
];

export default {
    name: 'CreateGroupChatPopup',

    components: {
        Multiselect,
        VueCropper
    },

    data() {
        return {
            groupName: '',
            selectedUsers: [],
            userOptions: [],

            selectedFileName: '',
            selectedFileType: '',
            imageSource: '',
            croppedImage: '',
            croppedImagePreview: '',

            isLoadingUsers: false,
            isSubmitting: false,

            userLoadError: '',
            imageError: '',
            cropError: '',
            submitError: ''
        };
    },

    computed: {
        currentUserId() {
            return localStorage.getItem('LoginedUserID');
        },

        maxFileSizeMb() {
            return MAX_FILE_SIZE / (1024 * 1024);
        },

        selectedUsersText() {
            const count = this.selectedUsers.length;

            if (count === 0) {
                return 'No participants selected.';
            }

            if (count === 1) {
                return '1 participant selected.';
            }

            return count + ' participants selected.';
        },

        selectedUserIds() {
            return this.selectedUsers.map(function (user) {
                return user.id;
            });
        },

        participantIds() {
            const ids = this.selectedUserIds.slice();

            if (
                this.currentUserId &&
                !ids.some((id) => {
                    return (
                        String(id) ===
                        String(this.currentUserId)
                    );
                })
            ) {
                ids.unshift(this.currentUserId);
            }

            return ids;
        }
    },

    mounted() {
        this.addGlobalListeners();
        this.loadUsers();
    },

    beforeDestroy() {
        this.removeGlobalListeners();
        this.clearImageState();
    },

    methods: {
        async loadUsers() {
            this.isLoadingUsers = true;
            this.userLoadError = '';

            try {
                const response = await this.$store.dispatch(
                    'isConditionalListOfAllUsersFun'
                );

                const users = this.extractResponseData(response);

                if (!Array.isArray(users)) {
                    throw new Error(
                        'The user list could not be loaded.'
                    );
                }

                this.userOptions = users
                    .filter(function (user) {
                        return (
                            user &&
                            user.id !== undefined &&
                            user.id !== null
                        );
                    })
                    .map(function (user) {
                        return {
                            id: user.id,
                            first_name:
                                user.first_name || '',
                            last_name:
                                user.last_name || '',
                            email: user.email || ''
                        };
                    });
            } catch (error) {
                this.userLoadError = this.getErrorMessage(
                    error,
                    'Unable to load users. Please try again.'
                );
            } finally {
                this.isLoadingUsers = false;
            }
        },

        getUserFullName(user) {
            if (!user) {
                return '';
            }

            const name = [
                user.first_name || '',
                user.last_name || ''
            ]
                .join(' ')
                .trim();

            return name || user.email || 'Unknown user';
        },

        getUserLabel(user) {
            if (!user) {
                return '';
            }

            const name = this.getUserFullName(user);

            if (user.email && name !== user.email) {
                return name + ' - ' + user.email;
            }

            return name;
        },

        handleFileUpload(event) {
            const input = event.target;
            const file =
                input.files && input.files.length
                    ? input.files[0]
                    : null;

            this.imageError = '';
            this.cropError = '';

            if (!file) {
                return;
            }

            if (
                ALLOWED_IMAGE_TYPES.indexOf(file.type) === -1
            ) {
                this.imageError =
                    'Please select a JPEG or PNG image.';

                this.resetFileInput();
                return;
            }

            if (file.size > MAX_FILE_SIZE) {
                this.imageError =
                    'The image must be smaller than ' +
                    this.maxFileSizeMb +
                    ' MB.';

                this.resetFileInput();
                return;
            }

            if (typeof FileReader !== 'function') {
                this.imageError =
                    'Image upload is not supported by this browser.';

                this.resetFileInput();
                return;
            }

            this.selectedFileName = file.name;
            this.selectedFileType = file.type;

            const reader = new FileReader();

            reader.onload = (readerEvent) => {
                const result =
                    readerEvent &&
                    readerEvent.target &&
                    readerEvent.target.result;

                if (!result) {
                    this.imageError =
                        'Unable to read the selected image.';

                    return;
                }

                this.imageSource = result;
            };

            reader.onerror = () => {
                this.imageError =
                    'Unable to read the selected image.';

                this.clearImageState();
            };

            reader.readAsDataURL(file);
        },

        cropImage() {
            const cropper = this.$refs.cropper;

            if (!cropper) {
                this.cropError =
                    'The image cropper is unavailable.';

                return;
            }

            this.cropError = '';

            try {
                const canvas = cropper.getCroppedCanvas({
                    width: 400,
                    height: 400,
                    imageSmoothingEnabled: true,
                    imageSmoothingQuality: 'high'
                });

                if (!canvas) {
                    throw new Error(
                        'Unable to crop the selected image.'
                    );
                }

                const outputType =
                    this.selectedFileType === 'image/png'
                        ? 'image/png'
                        : 'image/jpeg';

                const quality =
                    outputType === 'image/jpeg'
                        ? 0.9
                        : undefined;

                const dataUrl = canvas.toDataURL(
                    outputType,
                    quality
                );

                this.croppedImagePreview = dataUrl;
                this.croppedImage =
                    this.removeDataUrlPrefix(dataUrl);
                this.imageSource = '';

                this.$nextTick(() => {
                    this.resetCropper();
                });
            } catch (error) {
                this.cropError = this.getErrorMessage(
                    error,
                    'Unable to crop the image.'
                );
            }
        },

        cancelCrop() {
            this.imageSource = '';
            this.selectedFileName = '';
            this.selectedFileType = '';
            this.cropError = '';

            this.resetFileInput();

            this.$nextTick(() => {
                this.resetCropper();
            });
        },

        removeSelectedImage() {
            if (this.isSubmitting) {
                return;
            }

            this.clearImageState();
        },

        async submitGroup() {
            if (this.isSubmitting) {
                return;
            }

            this.submitError = '';

            const isValid =
                await this.$validator.validateAll();

            if (!isValid) {
                return;
            }

            if (!this.groupName.trim()) {
                return;
            }

            this.isSubmitting = true;

            try {
                const createResponse =
                    await this.$store.dispatch(
                        'chatRoomCreatedFun',
                        {
                            name: this.groupName.trim(),
                            is_support_chat: false,
                            is_group_chat: true,
                            users: this.participantIds
                        }
                    );

                const createdGroup =
                    this.extractResponseData(createResponse);

                if (
                    !createdGroup ||
                    createdGroup.id === undefined ||
                    createdGroup.id === null
                ) {
                    throw new Error(
                        'The group was created, but its ID was not returned.'
                    );
                }

                let successResponse = createResponse;

                if (this.croppedImage) {
                    successResponse =
                        await this.uploadGroupImage(
                            createdGroup.id
                        );
                }

                toast.success(
                    this.getSuccessMessage(
                        successResponse,
                        'Group created successfully.'
                    )
                );

                await this.refreshRoomLists();

                this.closeModal(true);
            } catch (error) {
                this.submitError = this.getErrorMessage(
                    error,
                    'Unable to create the group. Please try again.'
                );

                toast.error(this.submitError);
            } finally {
                this.isSubmitting = false;
            }
        },

        async uploadGroupImage(groupId) {
            return this.$store.dispatch(
                'updateGroupChatDetails',
                {
                    data: {
                        id: groupId,
                        data: {
                            group_image: this.croppedImage,
                            filename:
                                this.selectedFileName ||
                                this.getDefaultFileName()
                        }
                    }
                }
            );
        },

        async refreshRoomLists() {
            try {
                await Promise.all([
                    this.$store.dispatch(
                        'fetchPinnedChatRooms'
                    ),
                    this.$store.dispatch(
                        'fetchNormalChatRooms'
                    )
                ]);
            } catch (error) {
                console.error(
                    'Unable to refresh room lists:',
                    error
                );
            }
        },

        extractResponseData(response) {
            if (
                response &&
                response.data &&
                response.data.data !== undefined
            ) {
                return response.data.data;
            }

            return null;
        },

        removeDataUrlPrefix(dataUrl) {
            if (!dataUrl || typeof dataUrl !== 'string') {
                return '';
            }

            return dataUrl.replace(
                /^data:image\/[a-zA-Z0-9.+-]+;base64,/,
                ''
            );
        },

        getDefaultFileName() {
            return this.selectedFileType === 'image/png'
                ? 'group-image.png'
                : 'group-image.jpg';
        },

        getSuccessMessage(response, fallbackMessage) {
            if (response && response.data) {
                return (
                    response.data.msg ||
                    response.data.message ||
                    fallbackMessage
                );
            }

            return fallbackMessage;
        },

        getErrorMessage(error, fallbackMessage) {
            if (
                error &&
                error.response &&
                error.response.data
            ) {
                return (
                    error.response.data.msg ||
                    error.response.data.message ||
                    fallbackMessage
                );
            }

            if (error && error.data) {
                return (
                    error.data.msg ||
                    error.data.message ||
                    fallbackMessage
                );
            }

            if (error && error.message) {
                return error.message;
            }

            return fallbackMessage;
        },

        resetCropper() {
            const cropper = this.$refs.cropper;

            if (
                cropper &&
                typeof cropper.reset === 'function'
            ) {
                cropper.reset();
            }
        },

        resetFileInput() {
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        clearImageState() {
            this.imageSource = '';
            this.croppedImage = '';
            this.croppedImagePreview = '';
            this.selectedFileName = '';
            this.selectedFileType = '';
            this.imageError = '';
            this.cropError = '';

            this.resetFileInput();
        },

        handleKeydown(event) {
            if (event.key !== 'Escape') {
                return;
            }

            if (this.imageSource) {
                this.cancelCrop();
                return;
            }

            this.closeModal();
        },

        addGlobalListeners() {
            document.body.classList.add(
                BODY_SCROLL_CLASS
            );

            document.addEventListener(
                'keydown',
                this.handleKeydown
            );
        },

        removeGlobalListeners() {
            document.body.classList.remove(
                BODY_SCROLL_CLASS
            );

            document.removeEventListener(
                'keydown',
                this.handleKeydown
            );
        },

        closeModal(forceClose) {
            if (this.isSubmitting && forceClose !== true) {
                return;
            }

            this.removeGlobalListeners();
            this.clearImageState();

            bus.$emit('createGroupPopupBus', false);
        }
    }
};
</script>

<style lang="scss" scoped>
$overlay: rgba(0, 0, 0, 0.92);
$background: #000000;
$panel-background: #1b1b1d;
$text-primary: #ffffff;
$text-secondary: #aaaaaa;
$accent: #ffd400;
$primary: #87cefa;
$danger: #d94848;
$error: #ff6b6b;
$border: rgba(255, 255, 255, 0.1);
$input-background: rgba(255, 255, 255, 0.08);
$button-background: rgba(255, 255, 255, 0.12);

.create-group-modal {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    overflow-y: auto;
    color: $text-primary;
    background-color: $overlay;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;

    &__dialog {
        width: 100%;
        max-width: 640px;
        margin: auto;
        overflow: visible;
        border-radius: 5px;
        background-color: $background;
        box-shadow: 0 0 13px rgba(255, 213, 0, 0.6);
    }

    &__header {
        position: relative;
        display: flex;
        align-items: center;
        min-height: 60px;
        padding: 15px 55px 15px 18px;
        border-bottom: 1px solid $border;
        // background-color: $panel-background;
        border-radius: 5px 5px 0 0;
    }

    &__title {
        margin: 0;
        color: $text-primary;
        font-size: 22px;
        // font-weight: 600;
        line-height: 1.3;
    }

    &__close {
        position: absolute;
        top: 50%;
        right: 14px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        padding: 0;
        border: 0;
        border-radius: 3px;
        color: $danger;
        background: transparent;
        cursor: pointer;
        transform: translateY(-50%);
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease,
            transform 0.2s ease;

        .fa {
            font-size: 20px;
        }

        &:hover:not(:disabled),
        &:focus:not(:disabled) {
            color: $primary;
            background-color: rgba(255, 255, 255, 0.08);
            transform: translateY(-50%) scale(1.08);
        }

        &:focus {
            outline: 2px solid $accent;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
    }

    &__body {
        padding: 20px;
        overflow: visible;
    }

    &__status {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 220px;
        color: $text-secondary;
        font-size: 16px;
        text-align: center;

        .fa {
            margin-bottom: 10px;
            color: $accent;
            font-size: 24px;
        }

        &--error {
            color: $error;

            .fa {
                color: $error;
            }
        }
    }

    &__retry {
        min-width: 90px;
        margin-top: 15px;
        padding: 8px 15px;
        border: 0;
        border-radius: 3px;
        color: #22292f;
        background-color: $primary;
        font-family: inherit;
        cursor: pointer;

        &:hover,
        &:focus {
            background-color: $accent;
        }
    }
}

.create-group-form {
    overflow: visible;

    &__field {
        position: relative;
        margin-bottom: 20px;
        overflow: visible;
    }

    &__label {
        display: block;
        margin-bottom: 6px;
        color: $text-primary;
        font-size: 16px;
        line-height: 1.3;
    }

    &__input {
        width: 100%;
        height: auto;
        padding: 10px 15px;
        border: 1px solid $border;
        border-radius: 3px;
        color: $accent;
        background-color: $input-background;
        font-family: inherit;
        font-size: 16px;
        box-shadow: none;
        transition: border-color 0.2s ease;

        &:focus {
            border-color: $accent;
            outline: none;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.6;
        }

        &--invalid {
            border-color: $error;
        }
    }

    &__multiselect {
        position: relative;
        z-index: 10;

        &--invalid {
            /deep/ .multiselect__tags {
                border-color: $error;
            }
        }
    }

    &__error {
        display: block;
        margin-top: 6px;
        color: $error;
        font-size: 14px;
        line-height: 1.3;
    }

    &__hint {
        display: block;
        margin-top: 6px;
        color: $text-secondary;
        font-size: 13px;
        line-height: 1.4;
    }

    &__image-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
    }

    &__image-preview {
        flex: 0 0 70px;
        width: 70px;
        height: 70px;
        overflow: hidden;
        border: 1px solid $border;
        border-radius: 50%;
        background-color: $input-background;

        img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    &__file-input {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        border: 0;
        white-space: nowrap;
    }

    &__upload {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        min-height: 42px;
        padding: 9px 15px;
        border: 1px solid $border;
        border-radius: 3px;
        color: $text-primary;
        background-color: $button-background;
        font-size: 16px;
        line-height: 1;
        cursor: pointer;
        word-break: break-word;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            flex: 0 0 auto;
            margin-right: 8px;
        }

        &:hover {
            color: #22292f;
            background-color: $primary;
        }

        &--disabled {
            cursor: not-allowed;
            opacity: 0.55;
            pointer-events: none;
        }
    }

    &__remove-image {
        min-height: 38px;
        padding: 8px 13px;
        border: 0;
        border-radius: 3px;
        color: $text-primary;
        background-color: $danger;
        font-family: inherit;
        font-size: 14px;
        cursor: pointer;

        &:hover:not(:disabled),
        &:focus:not(:disabled) {
            background-color: #ff5c5c;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }
    }

    &__submit-error {
        margin: 0 0 16px;
        color: $error;
        font-size: 14px;
        line-height: 1.4;
        text-align: center;
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 4px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 125px;
        min-height: 42px;
        padding: 9px 15px;
        border: 0;
        border-radius: 3px;
        color: $text-primary;
        font-family: inherit;
        font-size: 16px;
        line-height: 1;
        cursor: pointer;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 7px;
        }

        &:focus {
            outline: 2px solid $accent;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--cancel {
            background-color: $button-background;

            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                color: #22292f;
                background-color: $accent;
            }
        }

        &--submit {
            color: #22292f;
            background-color: $primary;

            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                background-color: $accent;
            }
        }
    }
}

.participant-option {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &__name {
        overflow: hidden;
        font-weight: 600;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__email {
        margin-top: 2px;
        overflow: hidden;
        font-size: 13px;
        opacity: 0.8;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.participant-tag {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    margin: 2px 5px 2px 0;
    padding: 5px 7px 5px 9px;
    border-radius: 3px;
    color: #ffffff;
    background-color: #41b883;
    font-size: 15px;

    &__text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__remove {
        margin-left: 7px;
        padding: 0;
        border: 0;
        color: inherit;
        background: transparent;
        font-size: 18px;
        line-height: 1;
        cursor: pointer;

        &:hover,
        &:focus {
            color: #cc0000;
        }
    }
}

.create-group-form__multiselect /deep/ {
    .multiselect {
        min-height: 48px;
        color: $text-primary;
    }

    .multiselect__tags {
        min-height: 48px;
        padding: 10px 42px 6px 12px;
        border: 1px solid $border;
        border-radius: 3px;
        color: $text-primary;
        background-color: $input-background;
        font-size: 16px;
    }

    .multiselect__input,
    .multiselect__single {
        margin: 0;
        padding: 0;
        color: $text-primary;
        background-color: transparent;
        font-family: inherit;

        &::placeholder {
            color: $text-secondary;
        }
    }

    .multiselect__placeholder {
        margin: 0;
        padding: 3px 0 0;
        color: $text-secondary;
    }

    .multiselect__content-wrapper {
        z-index: 100;
        border: 1px solid #3d3d3d;
        border-top: 0;
        background-color: #242424;
    }

    .multiselect__content {
        width: 100%;
    }

    .multiselect__option {
        padding: 5px 14px;
        color: $accent;
        background-color: #242424;
        white-space: normal;
    }

    .multiselect__option--highlight {
        color: #22292f;
        background-color: $primary;

        &::after {
            color: #22292f;
            background-color: transparent;
        }
    }

    .multiselect__option--selected {
        color: $accent;
        background-color: #313131;
    }

    .multiselect__option--selected.multiselect__option--highlight {
        color: #22292f;
        background-color: $primary;
    }
}

.image-crop-modal {
    position: fixed;
    inset: 0;
    z-index: 1010;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    overflow-y: auto;
    background-color: rgba(0, 0, 0, 0.96);

    &__dialog {
        width: 100%;
        max-width: 430px;
        margin: auto;
        overflow: hidden;
        border-radius: 5px;
        background-color: $background;
        box-shadow: 0 0 13px rgba(255, 213, 0, 0.6);
    }

    &__header {
        padding: 15px 18px;
        border-bottom: 1px solid $border;
        background-color: $panel-background;
    }

    &__title {
        margin: 0;
        color: $text-primary;
        font-size: 21px;
        line-height: 1.3;
    }

    &__body {
        padding: 18px;
    }

    &__cropper {
        min-height: 340px;
        overflow: hidden;
        border: 1px solid $border;
        border-radius: 4px;
        background-color: #111111;
    }

    &__error {
        margin: 12px 0 0;
        color: $error;
        font-size: 14px;
        text-align: center;
    }

    &__actions {
        display: flex;
        justify-content: center;
        gap: 12px;
        margin-top: 16px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 125px;
        min-height: 42px;
        padding: 9px 15px;
        border: 0;
        border-radius: 3px;
        color: $text-primary;
        font-family: inherit;
        font-size: 16px;
        cursor: pointer;

        img {
            width: 21px;
            height: 21px;
            margin-right: 7px;
            object-fit: contain;
        }

        &--cancel {
            background-color: $danger;

            &:hover,
            &:focus {
                background-color: #ff5c5c;
            }
        }

        &--crop {
            background-color: $button-background;

            &:hover,
            &:focus {
                color: #22292f;
                background-color: $primary;

                img {
                    filter: invert(1);
                }
            }
        }
    }
}

.image-crop-modal__cropper /deep/ {
    .cropper-container {
        width: 100% !important;
        min-height: 340px;
    }

    .cropper-line,
    .cropper-point {
        background-color: $accent;
    }

    .cropper-view-box {
        outline-color: rgba(255, 212, 0, 0.8);
    }
}

@media (max-width: 600px) {
    .create-group-modal {
        align-items: flex-start;
        padding: 10px;

        &__dialog {
            margin-top: auto;
            margin-bottom: auto;
        }

        &__header {
            min-height: 54px;
            padding: 13px 50px 13px 14px;
        }

        &__title {
            font-size: 20px;
        }

        &__body {
            padding: 14px;
        }
    }

    .create-group-form {
        &__actions {
            flex-direction: column-reverse;
        }

        &__button {
            width: 100%;
        }

        &__image-row {
            align-items: flex-start;
            flex-direction: column;
        }

        &__upload {
            width: 100%;
            justify-content: center;
        }
    }

    .image-crop-modal {
        padding: 10px;

        &__cropper {
            min-height: 280px;
        }

        &__actions {
            flex-direction: column-reverse;
        }

        &__button {
            width: 100%;
        }
    }

    .image-crop-modal__cropper /deep/ {
        .cropper-container {
            min-height: 280px;
        }
    }
}
</style>